import polygonClipping from 'polygon-clipping'
import { ringWinding } from './geo'
import type { GeoPt } from './types'

// polygon-clipping only ships a default export at runtime (its .d.ts declares
// named exports, which typechecks under `moduleResolution: bundler` but breaks
// the production Rollup build). Import the default and read `.union` off it.
type Pair = [number, number]

/** A ring needs at least three points to enclose any area. */
export const MIN_RING_POINTS = 3
/** A union only means anything once at least two parcels are selected. */
export const MIN_PARCELS_TO_UNION = 2

/**
 * True Boolean union of area outlines, keeping every part.
 *
 * When the areas form one connected shape with no enclosed gaps this is a single
 * merged outer ring with the shared seams dissolved — unlike a convex hull, it
 * does not swallow the land BETWEEN them.
 *
 * A DISJOINT union and an ENCLOSED HOLE are both real shapes a hunting ground can
 * have, not failures. The app carries them as one ground holding several outer
 * rings, with the enclosed gaps painted as no-hunting areas. Returning null for
 * either — which a one-ring-per-ground model forced — sent the caller back to
 * drawing one polygon per stored record, which is the seam quilt this exists to
 * dissolve. A null result now means only that there was nothing to union.
 *
 * Remaining limitation: clean seams require adjacent areas to share coincident
 * edge vertices (the cadastral provider serves one shared boundary per edge, so
 * they normally do); outlines differing by sub-metre float amounts may leave a
 * minor seam artifact, or, if that gap makes them non-touching, come back as two
 * parts rather than one.
 */
export interface UnionedRings {
  /** One outer ring per disjoint part. Never empty. */
  outers: GeoPt[][]
  /** Interior holes, unassociated with a particular outer. */
  holes: GeoPt[][]
}

/**
 * Splits a flat ring list into whole polygons, each an outer ring followed by the
 * holes that fall inside it.
 *
 * The wire hands over outers and holes as one list with a count, and says nothing
 * about which hole belongs to which part — so a ground of two parcels where only
 * the second has a lake arrives as three rings and a "2". Containment recovers the
 * association: a hole is by definition strictly inside exactly one outer, so the
 * outer containing any one of its vertices is the one it belongs to. A hole inside
 * none of them is a contradiction in the data rather than a shape, and is dropped
 * instead of being attached to an arbitrary part where it would punch a gap through
 * ground that is really huntable.
 *
 * Ownership is decided by OVERLAP AREA: a hole belongs to the outer it overlaps most,
 * and to none if it overlaps nothing. Four point-sampling rules were tried here first
 * and every one of them lost a lake on some ordinary parcel shape, because they all rest
 * on `pointInPolygon`, which says of itself that a point on an edge "may fall either
 * side" - tolerable for a hit test, but here a wrong answer DELETES a ring.
 *
 * The first vertex: dropped a lake whose ring shares a vertex with the parcel boundary,
 * the ordinary cadastral encoding for water reaching the edge of a property. Any vertex:
 * fixed that and broke a worse case, because parcels tile - a lake touching the seam has
 * its shared vertices read as inside the NEIGHBOUR, and a hole attached to a parcel it
 * lies outside is not merely misfiled: the clipper subtracts nothing, the union consumes
 * the ring, and the lake is gone from every surface and from the printed sheet while the
 * API still subtracts it from the hectares. The vertex mean: picked a sibling parcel
 * sitting in a horseshoe's notch. Mean AND vertex: rejected the true owner of a lake in a
 * parcel's CORNER, where every vertex lies on a max-lat or max-lon edge and reads as
 * outside - so the mean stopped deciding anything and became only a veto, leaving the
 * discredited vertex rule to do the attaching.
 *
 * Overlap area has no boundary tie-break to get wrong. A lake inside a parcel overlaps it
 * entirely and its neighbours not at all, whatever their shared edges do, so the answer
 * never depends on which side of a line a vertex is deemed to fall. It costs one clip per
 * hole per outer, on a path that runs per data change rather than per frame. A hole
 * overlapping nothing is a contradiction in the data rather than a shape, and is dropped
 * rather than attached to an arbitrary part.
 */
/**
 * Twice the area a hole and an outer share, via the clipper both are already drawn with.
 * Zero when they only touch, which is the whole point: a shared edge contributes no
 * area, so a neighbour can never win ownership of a lake that merely abuts it.
 *
 * The clipper normalises its output to outers-positive and holes-negative, so summing
 * the SIGNED shoelace gives the true area rather than double-counting a hole in the
 * intersection. A throw means the two are not comparable, which is an answer of zero.
 */
function overlapArea(hole: readonly GeoPt[], outer: readonly GeoPt[]): number {
  const toPolygon = (ring: readonly GeoPt[]) => [closeRing(ring.map((p): Pair => [p.lon, p.lat]))]
  let clipped
  try {
    clipped = polygonClipping.intersection(toPolygon(hole), toPolygon(outer))
  } catch {
    return 0
  }
  let sum = 0
  for (const poly of clipped) {
    for (const ring of poly) {
      for (let i = 0; i < ring.length; i++) {
        const [ax, ay] = ring[i]
        const [bx, by] = ring[(i + 1) % ring.length]
        sum += ax * by - bx * ay
      }
    }
  }
  return Math.abs(sum)
}

/** The outer a hole overlaps most, or -1 if it overlaps none of them. */
function holeOwner(hole: readonly GeoPt[], polys: readonly GeoPt[][][]): number {
  let owner = -1
  let best = 0
  for (let i = 0; i < polys.length; i++) {
    const area = overlapArea(hole, polys[i][0])
    if (area > best) {
      best = area
      owner = i
    }
  }
  return owner
}

export function ringsToPolygons(rings: readonly GeoPt[][], outerCount: number): GeoPt[][][] {
  const usable = (r: readonly GeoPt[]) => r.length >= MIN_RING_POINTS
  const outers = rings.slice(0, Math.max(0, outerCount)).filter(usable)
  const holes = rings.slice(Math.max(0, outerCount)).filter(usable)
  const polys: GeoPt[][][] = outers.map((outer) => [outer])
  for (const hole of holes) {
    const owner = holeOwner(hole, polys)
    if (owner >= 0) polys[owner].push(hole)
  }
  return polys
}

/**
 * Turns a ring the way a non-zero fill needs it: outers counter-clockwise, holes
 * clockwise. Never in place — `ringsToPolygons` keeps every ring BY REFERENCE off
 * the caller's own state, so reversing one would rewrite stored geometry from
 * under the editor.
 *
 * A zero-area ring has no direction to correct and is passed through.
 */
function orient(ring: readonly GeoPt[], want: 1 | -1): GeoPt[] {
  const sign = Math.sign(ringWinding(ring))
  return sign === 0 || sign === want ? [...ring] : [...ring].reverse()
}
/**
 * The rings to DRAW for one set of parts: merged where the clipper can merge them,
 * kept whole where it cannot. Outers first, then holes, with the count that separates
 * them — the same pair every renderer in this package consumes.
 *
 * Every surface that draws a ground resolves its rings through here, which is the
 * point of the function existing rather than each caller doing the union itself. The
 * editor did its own, through a helper that answered a SINGLE ring or null and left
 * the caller to fall back to the raw per-member rings; so a property group whose union
 * came back disjoint or holed drew every parcel separately in the one place the user
 * actually draws the map — full-weight line on each internal seam, each parcel's halo
 * stacked along it — while the read-only map beside it showed the merged outline. That
 * divergence is the defect this whole change exists to remove, so there is now one
 * resolver and no second opinion.
 *
 * `unionAreaOutlines` answers null for a THROWN clip as well as for an empty result, so
 * null here keeps every part and gives up only the merge: losing a seam-dissolve is
 * cosmetic, losing a parcel is not.
 *
 * This used to add that polygon-clipping "does throw on real cadastral geometry". That
 * claim is withdrawn and should not be reinstated without new evidence: 40,000
 * randomised unions of self-intersecting blobs at Swedish lat/lon, snapped to five
 * different grids, plus twelve hand-built pathological cases (seam noise at 1e-9 deg, a
 * bow-tie, a 200-vertex collinear seam, a repeated vertex, a 1e-14 sliver) produced zero
 * throws and zero empty results. The fallback is kept because a clipper that gives up
 * must not cost a parcel, not because it is known to be reached.
 */

export function unionedRings(
  polys: readonly GeoPt[][][],
): { rings: GeoPt[][]; outerCount: number } {
  const union = polys.length > 1 ? unionAreaOutlines(polys) : null
  if (union) {
    return {
      rings: [...union.outers.map((r) => orient(r, 1)), ...union.holes.map((r) => orient(r, -1))],
      outerCount: union.outers.length,
    }
  }
  return {
    rings: [
      ...polys.map((poly) => orient(poly[0], 1)),
      ...polys.flatMap((poly) => poly.slice(1).map((hole) => orient(hole, -1))),
    ],
    outerCount: polys.length,
  }
}

/**
 * @param polys one entry per area, each a whole polygon set (see `ringsToPolygons`).
 */
export function unionAreaOutlines(polys: readonly GeoPt[][][]): UnionedRings | null {
  const polygons = polys
    .map((poly) => poly.filter((ring) => ring.length >= MIN_RING_POINTS))
    .filter((poly) => poly.length > 0)
    .map((poly) => poly.map((ring) => closeRing(ring.map((p): Pair => [p.lon, p.lat]))))
  if (polygons.length < MIN_PARCELS_TO_UNION) return null

  let result
  try {
    result = polygonClipping.union(polygons[0], ...polygons.slice(1))
  } catch {
    return null
  }
  if (!result.length) return null

  const toRing = (ring: Pair[]): GeoPt[] =>
    stripClosingPoint(ring.map(([lon, lat]): GeoPt => ({ lat, lon })))

  // Each result entry is [outerRing, ...holeRings].
  return {
    outers: result.map((poly) => toRing(poly[0])),
    holes: result.flatMap((poly) => poly.slice(1).map(toRing)),
  }
}

// polygon-clipping expects closed rings (first point repeated at the end).
function closeRing(ring: Pair[]): Pair[] {
  const [fx, fy] = ring[0]
  const [lx, ly] = ring[ring.length - 1]
  return fx === lx && fy === ly ? ring : [...ring, [fx, fy]]
}

// Our outlines are stored open; drop the closing vertex the union produces.
function stripClosingPoint(pts: GeoPt[]): GeoPt[] {
  if (pts.length < 2) return pts
  const first = pts[0]
  const last = pts[pts.length - 1]
  return first.lat === last.lat && first.lon === last.lon ? pts.slice(0, -1) : pts
}
